import type { HTMLAttributes } from 'react';

import { cn } from '~/lib/cn';
import type { VariantProps } from '~/lib/variants';
import { variants } from '~/lib/variants';

const avatarVariants = variants({
  base: [
    'flex',
    'items-center',
    'justify-center',
    'flex-shrink-0',
    'font-semibold',
  ],
  variants: {
    variant: {
      primary: ['text-strong-on-light', 'bg-primary-default'],
      secondary: ['text-strong-on-light', 'bg-secondary-default'],
      base: ['text-strong-on-dark', 'bg-level-03'],
    },
    size: {
      xl: ['w-[110px]', 'h-[110px]', 'text-display-md'],
      lg: ['w-[56px]', 'h-[56px]', 'text-display-sm'],
      md: ['w-2xl', 'h-2xl', 'text-body-md'],
      sm: ['w-[28px]', 'h-[28px]', 'text-body-xs'],
    },
    shape: {
      circle: ['rounded-full', 'aspect-square'],
      square: ['rounded-sm'],
    },
    hasLongInitials: {
      true: [],
      false: [],
    },
  },
  defaultVariants: {
    variant: 'base',
    size: 'md',
    shape: 'circle',
  },
  compoundVariants: [
    {
      size: 'lg',
      hasLongInitials: true,
      className: 'text-body-md',
    },
    {
      size: 'md',
      hasLongInitials: true,
      className: 'text-body-xs',
    },
    {
      size: 'sm',
      hasLongInitials: true,
      className: 'text-caption-sm',
    },
  ],
});

const getInitials = (name: string) => {
  const initials =
    name
      .match(/\S+/g)
      ?.slice(0, 4)
      .map((word) => word[0])
      .join('') || '';

  return initials.toLocaleUpperCase();
};

export interface AvatarProps
  extends HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof avatarVariants> {
  name: string;
}

export function Avatar({
  name,
  variant = 'base',
  size = 'md',
  shape = 'circle',
  className,
  ...props
}: AvatarProps) {
  const displayName = getInitials(name);
  const variantClasses = avatarVariants({
    variant,
    size,
    shape,
    hasLongInitials: displayName.length > 2,
  });

  return (
    <span className={cn(variantClasses, className)} {...props}>
      {displayName}
    </span>
  );
}

Avatar.displayName = 'Avatar';
